import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  assignLead,
  recordContactAttempt,
  searchLeads
} from '../../../../shared/services/data/lead.data';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { LeadUiModel, SlaState } from '../../../../shared/ui-models/lead.ui-model';
import { APP_ROUTES } from '../../../../shared/config/routes-config';
import { RouteConfig } from '../../../../shared/interfaces/routes-config.interface';
import { RootState } from '../../../../shared/redux/reducers';
import { birdToast } from '../../../../shared/components/core-ui/toast';
import { cn } from '../../../../utils/cn';
import { LeadVertical } from '../../../shared/config/role-dashboard.config';
import { TabBar } from './tab-bar.component';
import { BUTTON, BUTTON_DARK, CARD, CHIP, duration, titleCase } from './dashboard-ui';

type Status = 'new' | 'pending' | 'reopened' | 'not_reachable';
type Scope = 'mine' | 'everyone';
export type LeadCounts = Partial<Record<Status, number>>;

const STATUSES: Status[] = ['new', 'pending', 'reopened', 'not_reachable'];
const PAGE = 15;

const VERTICAL_STYLE: Record<string, string> = {
  furnishing: 'bg-blue text-neutral-500',
  acquisition: 'bg-primary-50 text-primary-700',
  reservation: 'bg-creams-500 text-creams-900'
};

const SLA: Record<SlaState, { label: string; chip: string; dot: string }> = {
  [SlaState.MET]: {
    label: 'Met',
    chip: 'bg-primary-50 text-primary-700',
    dot: 'bg-action-success'
  },
  [SlaState.BREACHED]: {
    label: 'Breached',
    chip: 'bg-secondary-50 text-secondary-700',
    dot: 'bg-action-danger'
  },
  [SlaState.AT_RISK]: {
    label: 'At risk',
    chip: 'bg-creams-500 text-creams-900',
    dot: 'bg-action-alert'
  },
  [SlaState.AWAITING]: {
    label: 'Awaiting',
    chip: 'bg-blue text-neutral-500',
    dot: 'bg-primary-400'
  },
  [SlaState.UNKNOWN]: { label: '—', chip: 'bg-neutral-50 text-neutral-300', dot: 'bg-neutral-100' }
};

const detailsPath = (id: string) =>
  (APP_ROUTES.LEAD_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(':leadId', id);

type LeadsWidgetProps = {
  verticals: LeadVertical[];
  userId: string;
  onCounts?: (counts: LeadCounts) => void;
};

const LeadsWidgetComponent = ({ verticals, userId, onCounts }: LeadsWidgetProps) => {
  const history = useHistory();
  const [status, setStatus] = useState<Status>('new');
  const [scope, setScope] = useState<Scope>('mine');
  const [vertical, setVertical] = useState<LeadVertical | ''>(
    verticals.length === 1 ? verticals[0] : ''
  );
  const [leads, setLeads] = useState<LeadUiModel[]>();
  const [counts, setCounts] = useState<LeadCounts>({});
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<string>();

  const load = useCallback(() => {
    const base = {
      vertical: vertical || undefined,
      assignedTo: scope === 'mine' ? userId : undefined
    };
    Promise.all(
      STATUSES.map((s) => searchLeads({ ...base, status: s }, s === status ? PAGE : 1, 0))
    ).then((results) => {
      if (results.some(isErrorInfo)) {
        setFailed(true);
        return;
      }
      const ok = results as { data: LeadUiModel[]; total: number }[];
      setFailed(false);
      const next: LeadCounts = STATUSES.reduce((all, s, i) => ({ ...all, [s]: ok[i].total }), {});
      setCounts(next);
      onCounts?.(next);
      setLeads(ok[STATUSES.indexOf(status)].data);
    });
  }, [status, scope, vertical, userId, onCounts]);

  useEffect(load, [load]);

  const act = (lead: LeadUiModel, action: 'take' | 'call') => {
    setBusyId(lead.id);
    const request = action === 'take' ? assignLead(lead.id, userId) : recordContactAttempt(lead.id);
    request.then((result) => {
      setBusyId(undefined);
      if (isErrorInfo(result)) {
        birdToast.error(action === 'take' ? 'Could not take the lead' : 'Could not log the call');
        return;
      }
      birdToast.success(action === 'take' ? `Lead ${lead.shortId} is yours` : 'Call logged');
      load();
    });
  };

  const select =
    'h-9 cursor-pointer rounded-full border border-gray-200 bg-white px-3.5 text-[13px] font-gilroy-medium text-neutral-500 outline-none focus:border-primary-300';
  const th =
    'whitespace-nowrap px-4 py-3 text-[12px] font-gilroy-medium uppercase tracking-[0.04em] text-neutral-300';
  const td = 'whitespace-nowrap px-4 py-3.5 text-[14px] align-middle';
  const showVertical = verticals.length > 1;

  return (
    <section className={CARD}>
      <div className="flex flex-col gap-3 px-5 pt-5 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2.5">
          <h2 className="m-0 text-[18px] font-gilroy-bold text-neutral-500">Leads</h2>
          {!showVertical && verticals[0] && (
            <span className={cn(CHIP, VERTICAL_STYLE[verticals[0]])}>
              {titleCase(verticals[0])}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {showVertical && (
            <select
              aria-label="Verticals"
              value={vertical}
              onChange={(e) => setVertical(e.target.value as LeadVertical | '')}
              className={select}
            >
              <option value="">All verticals</option>
              {verticals.map((v) => (
                <option key={v} value={v}>
                  {titleCase(v)}
                </option>
              ))}
            </select>
          )}
          <select
            aria-label="Owner"
            value={scope}
            onChange={(e) => setScope(e.target.value as Scope)}
            className={select}
          >
            <option value="mine">Mine</option>
            <option value="everyone">Everyone</option>
          </select>
        </div>
      </div>
      <div className="mt-2">
        <TabBar
          tabs={STATUSES.map((s) => ({ key: s, label: titleCase(s), count: counts[s] }))}
          active={status}
          onChange={setStatus}
        />
      </div>
      {failed ? (
        <p className="px-5 py-12 text-center text-[14px] text-neutral-300">
          Leads could not be loaded.
        </p>
      ) : !leads ? (
        <div className="space-y-3 px-5 py-5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block h-10 animate-pulse rounded bg-gray-200 motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : !leads.length ? (
        <p className="px-5 py-12 text-center text-[14px] text-neutral-300">No leads</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse text-left">
            <thead className="bg-creams-100">
              <tr>
                <th className={th}>#</th>
                {showVertical && <th className={th}>Vertical</th>}
                <th className={th}>Lead</th>
                <th className={th}>SLA</th>
                <th className={th}>Attempts</th>
                <th className={th}>Owner</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => {
                const sla = SLA[lead.slaState];
                const mine = lead.assignedToId === userId;
                return (
                  <tr
                    key={lead.id}
                    onClick={() => history.push(detailsPath(lead.id))}
                    className="cursor-pointer border-t border-gray-200 transition-colors hover:bg-creams-100"
                  >
                    <td className={cn(td, 'tabular-nums text-neutral-300')}>{lead.shortId}</td>
                    {showVertical && (
                      <td className={td}>
                        <span className={cn(CHIP, VERTICAL_STYLE[lead.vertical])}>
                          {titleCase(lead.vertical)}
                        </span>
                      </td>
                    )}
                    <td className={td}>
                      <div className="font-gilroy-medium text-neutral-500">
                        {lead.name || lead.phone}
                      </div>
                      <div className="text-[12px] tabular-nums text-neutral-300">
                        {[
                          lead.source,
                          `${duration(Date.now() - new Date(lead.createdAt).getTime())} ago`
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td className={td}>
                      <span className={cn(CHIP, 'gap-1.5', sla.chip)}>
                        <span className={cn('h-1.5 w-1.5 rounded-full', sla.dot)} />
                        {sla.label}
                      </span>
                    </td>
                    <td className={td}>
                      <span
                        className={cn(
                          CHIP,
                          lead.remainingContactAttempts
                            ? 'bg-creams-500 text-creams-900'
                            : 'bg-neutral-50 text-neutral-400'
                        )}
                      >
                        {lead.remainingContactAttempts} left
                      </span>
                    </td>
                    <td
                      className={cn(
                        td,
                        lead.assignedToId ? 'text-neutral-500' : 'text-neutral-300'
                      )}
                    >
                      {mine ? 'You' : lead.assigneeName || 'Unassigned'}
                    </td>
                    <td className={cn(td, 'text-right')}>
                      {!lead.assignedToId ? (
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            act(lead, 'take');
                          }}
                          className={BUTTON_DARK}
                        >
                          Take
                        </button>
                      ) : mine && lead.remainingContactAttempts > 0 ? (
                        <button
                          type="button"
                          disabled={busyId === lead.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            act(lead, 'call');
                          }}
                          className={BUTTON}
                        >
                          Log call
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export const LeadsWidget = connect((state: RootState) => ({ userId: state.auth.userId || '' }))(
  LeadsWidgetComponent
);
